/**
 * DRILL-DOWN PANEL
 *
 * Right-side slide-over that lists the executions behind a chart segment.
 * Opened via `openDrillDown({ level, id, status })` Redux action — typically
 * from a chart click handler. Panel self-fetches the items via
 * `fetchDrillDownAsync`.
 *
 * Kept intentionally light-weight — a simple table of executions with basic
 * metadata. If the server hit its row cap the panel shows a "truncated" hint.
 */

import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dialog } from '@headlessui/react'
import Spinner from '@atlaskit/spinner'
import Button from '@atlaskit/button'
import CrossIcon from '@atlaskit/icon/glyph/cross'
import { token } from '@atlaskit/tokens'

import {
  closeDrillDown,
  fetchDrillDownAsync,
  selectDrillDown,
  selectScenarioReport
} from '../../../store/slices/reportsSlice'

const formatDateTime = (v) => {
  if (!v) return '—'
  return new Date(v).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

const statusBadgeStyle = (color) => ({
  display: 'inline-block',
  padding: '2px 8px',
  fontSize: '11px',
  fontWeight: 600,
  borderRadius: '3px',
  backgroundColor: color || token('color.background.neutral', '#F4F5F7'),
  color: color ? '#FFFFFF' : token('color.text.subtle', '#6B778C')
})

const DrillDownPanel = () => {
  const dispatch = useDispatch()
  const { open, criteria, items, loading, error } = useSelector(selectDrillDown)
  const scenarioReport = useSelector(selectScenarioReport)

  // Map testRunId -> ordinal (1-based) for the scenario in view. Only populated
  // when the scope is 'scenario' — for release/project drill-downs the runs
  // span multiple scenarios so a stable ordinal isn't meaningful.
  const runOrdinalByRunId = React.useMemo(() => {
    const map = new Map()
    if (criteria?.level !== 'scenario') return map
    const runs = scenarioReport?.runBreakdown || []
    runs.forEach((r, idx) => {
      if (r.runId != null) map.set(r.runId, idx + 1)
    })
    return map
  }, [scenarioReport, criteria])

  useEffect(() => {
    if (open && criteria) {
      dispatch(fetchDrillDownAsync(criteria))
    }
  }, [dispatch, open, criteria])

  const handleClose = () => dispatch(closeDrillDown())

  const title = (() => {
    if (!criteria) return 'Drill-down'
    const statusPart = Array.isArray(criteria.status) && criteria.status.length
      ? ` · ${criteria.status.join(' / ')}`
      : ''
    return `${titleCase(criteria.level)}${statusPart}`
  })()

  return (
    <Dialog open={open} onClose={handleClose} className="drilldown-dialog-root">
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1200
        }}
        aria-hidden="true"
      />

      {/* Side panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '520px',
          maxWidth: '100%',
          zIndex: 1201,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
          boxShadow: '-8px 0 16px -4px rgba(9, 30, 66, 0.25)'
        }}
      >
        {/* Header */}
        <div
          style={{
            flex: '0 0 auto',
            padding: '16px 20px',
            borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Dialog.Title
            style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: 600,
              color: token('color.text', '#172B4D')
            }}
          >
            {title}
          </Dialog.Title>
          <Button
            appearance="subtle"
            spacing="compact"
            iconBefore={<CrossIcon label="Close" size="small" />}
            onClick={handleClose}
          />
        </div>

        {/* Body */}
        <div style={{ flex: '1 1 auto', overflow: 'auto', padding: '16px 20px' }}>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Spinner size="large" />
            </div>
          )}

          {!loading && error && (
            <div style={{ color: token('color.text.danger', '#DE350B'), fontSize: '13px' }}>
              {error}
            </div>
          )}

          {!loading && !error && items.length === 0 && (
            <div
              style={{
                padding: '32px',
                textAlign: 'center',
                color: token('color.text.subtle', '#6B778C'),
                fontSize: '13px'
              }}
            >
              No executions match this selection.
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {items.map((item) => (
                <div
                  key={item.executionId}
                  style={{
                    padding: '10px 12px',
                    border: `1px solid ${token('color.border', '#DFE1E6')}`,
                    borderRadius: '4px',
                    backgroundColor: token('elevation.surface', '#FFFFFF')
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={statusBadgeStyle(item.statusColor)}>{item.status || '—'}</span>
                    {item.scenarioKey && (
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '1px 6px',
                          borderRadius: '3px',
                          backgroundColor: token('color.background.neutral', '#F4F5F7'),
                          color: token('color.text.subtle', '#6B778C')
                        }}
                      >
                        {item.scenarioKey}
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: '13px',
                        color: token('color.text', '#172B4D'),
                        fontWeight: 500
                      }}
                    >
                      {item.testCaseName || item.scenarioName || `Execution #${item.executionId}`}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      color: token('color.text.subtle', '#6B778C'),
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}
                  >
                    {/*
                      Sequential run label (e.g. "Run 02") when we're drilled
                      into a single scenario. For release/project scope we'd
                      need to know the run's position within its own scenario,
                      which isn't part of this payload — so omit the run
                      reference rather than showing a raw DB id.
                    */}
                    {item.testRunId && runOrdinalByRunId.get(item.testRunId) && (
                      <span>
                        Run {String(runOrdinalByRunId.get(item.testRunId)).padStart(2, '0')}
                      </span>
                    )}
                    <span>· {formatDateTime(item.executionTime)}</span>
                    {item.executorAccountId && (
                      <span>
                        · by{' '}
                        <span style={{ fontFamily: 'var(--ds-font-family-code, monospace)' }}>
                          @{item.executorAccountId}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  )
}

function titleCase(str) {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export default DrillDownPanel
